import React, { useState, useEffect, useCallback } from "react";
import { Container, Card, CardBody, Input, Button, Spinner, TabContent, TabPane, Table, Badge, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { ShieldCheck, Clock, Search, Check, X, FileText, MapPin, User, Mail, Phone, Calendar, Hash, AlertCircle, Eye, Building2, CreditCard, BadgeCheck, UserX, UserCheck, Lock, XCircle, MessageSquare } from "lucide-react";
import { toast } from "react-toastify";
import Handler from "../../Helpers/Handler";
import AdminHeader from "./Header";
import "../../Assets/css/style.css";

/* ── Reject Modal ─────────────────────────────── */
const RejectModal = ({ business, isOpen, toggle, onDone }) => {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleReject = async () => {
    if (!reason.trim()) return toast.error("Reason is required");
    setSubmitting(true);
    const res = await Handler({
      method: "POST",
      url: "/verified_batch/verify_respond.php",
      data: { user_id: business.user_id, action: "REJECT", reason: reason.trim() },
    });
    setSubmitting(false);
    if (res?.success) {
      toast.success("Business rejected and account suspended");
      onDone();
    } else {
      toast.error(res?.message || "Failed");
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered className="border-0">
      <ModalHeader toggle={toggle} className="bg-light border-0">
        <div className="d-flex align-items-center gap-2 text-danger">
          <AlertCircle size={20} />
          <span className="fw-bold">Reject & Suspend Account</span>
        </div>
      </ModalHeader>
      <ModalBody className="px-4 pb-4">
        {/* ⚠️ Consequence Warning Banner */}
        <div className="consequence-banner mb-3">
          <Lock size={15} className="flex-shrink-0 mt-1" />
          <div>
            <div className="fw-bold" style={{ fontSize: "0.85rem" }}>Account will be suspended</div>
            <div style={{ fontSize: "0.78rem", opacity: 0.85 }}>
              Rejecting verification will <strong>block this user from logging in</strong> until re-reviewed.
            </div>
          </div>
        </div>

        <div className="mb-3">
          <p className="text-muted small mb-1">Confirming rejection for:</p>
          <h6 className="fw-bold text-navy">{business.business_name}</h6>
        </div>
        <div className="form-group">
          <label className="text-navy small fw-bold mb-2">Reason for Rejection *</label>
          <textarea
            className="form-control bg-light border-0"
            placeholder="e.g. Invalid GST number, license expired..."
            rows={4}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={300}
            style={{ fontSize: "0.9rem" }}
          />
          <div className="text-end text-muted mt-1" style={{ fontSize: "0.7rem" }}>
            {reason.length}/300 characters
          </div>
        </div>
      </ModalBody>
      <ModalFooter className="border-0 pt-0">
        <Button color="light" onClick={toggle} disabled={submitting}>Cancel</Button>
        <Button color="danger" onClick={handleReject} disabled={submitting || !reason.trim()}>
          {submitting ? "Processing..." : <><Lock size={13} className="me-1" /> Reject & Suspend</>}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

/* ── Pending Detail Modal ─────────────────────── */
const PendingDetailModal = ({ business, isOpen, toggle, onApprove, onReject, approving }) => {
  if (!business) return null;

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="lg" className="border-0 detail-modal">
      <ModalHeader toggle={toggle} className="border-0 pb-0">
        <div className="d-flex align-items-center gap-2">
          <div className="icon-box-navy"><Eye size={18} /></div>
          <div>
            <h5 className="fw-bold text-navy mb-0">{business.business_name}</h5>
            <span className="badge-pending-sm">Awaiting Verification — Account Inactive</span>
          </div>
        </div>
      </ModalHeader>
      <ModalBody className="px-4 pb-2">

        {/* Approval consequence notice */}
        <div className="approval-notice-row mb-3">
          <div className="notice-pill approve-pill">
            <UserCheck size={13} /> Approve → Activates account &amp; grants platform access
          </div>
          <div className="notice-pill reject-pill">
            <UserX size={13} /> Reject → Suspends account &amp; blocks login
          </div>
        </div>

        <div className="detail-grid">

          {/* Business Info */}
          <div className="detail-section">
            <div className="detail-section-title">
              <Building2 size={14} /> Business Info
            </div>
            <div className="detail-rows">
              <div className="detail-row">
                <span className="detail-label">Business Name</span>
                <span className="detail-value fw-bold">{business.business_name || "—"}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">City</span>
                <span className="detail-value"><MapPin size={12} className="me-1 text-muted" />{business.city || "—"}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Requested On</span>
                <span className="detail-value">
                  <Calendar size={12} className="me-1 text-muted" />
                  {business.created_at ? new Date(business.created_at).toLocaleDateString("en-IN") : "—"}
                </span>
              </div>
            </div>
          </div>

          {/* Owner Info */}
          <div className="detail-section">
            <div className="detail-section-title">
              <User size={14} /> Owner Details
            </div>
            <div className="detail-rows">
              <div className="detail-row">
                <span className="detail-label">Owner Name</span>
                <span className="detail-value fw-bold">{business.owner_name || "—"}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Email</span>
                <span className="detail-value"><Mail size={12} className="me-1 text-muted" />{business.email || "—"}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Mobile</span>
                <span className="detail-value"><Phone size={12} className="me-1 text-muted" />{business.mobile || "—"}</span>
              </div>
            </div>
          </div>

          {/* Documents */}
          <div className="detail-section full-width">
            <div className="detail-section-title">
              <FileText size={14} /> Documents & Credentials
            </div>
            <div className="detail-rows">
              <div className="detail-row">
                <span className="detail-label">GST Number</span>
                <span className="detail-value mono-val">
                  <Hash size={12} className="me-1 text-muted" />
                  {business.gst_number || <span className="text-muted fst-italic">Not provided</span>}
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">License Number</span>
                <span className="detail-value mono-val">
                  <CreditCard size={12} className="me-1 text-muted" />
                  {business.license_number || <span className="text-muted fst-italic">Not provided</span>}
                </span>
              </div>
            </div>
          </div>

        </div>
      </ModalBody>
      <ModalFooter className="border-0 pt-0 px-4 pb-4 gap-2">
        <Button color="light" onClick={toggle}>Close</Button>
        <Button
          color="soft-danger"
          className="rounded-pill px-4"
          onClick={() => { toggle(); onReject(business); }}
          disabled={approving === business.user_id}
        >
          <UserX size={14} className="me-1" /> Reject & Suspend
        </Button>
        <Button
          color="soft-success"
          className="rounded-pill px-4"
          onClick={() => { toggle(); onApprove(business); }}
          disabled={approving === business.user_id}
        >
          {approving === business.user_id
            ? <Spinner size="sm" />
            : <><UserCheck size={14} className="me-1" /> Approve & Activate</>
          }
        </Button>
      </ModalFooter>
    </Modal>
  );
};

/* ── Verified Detail Modal ────────────────────── */
const VerifiedDetailModal = ({ business, isOpen, toggle }) => {
  if (!business) return null;

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="lg" className="border-0 detail-modal">
      <ModalHeader toggle={toggle} className="border-0 pb-0">
        <div className="d-flex align-items-center gap-2">
          <div className="icon-box-green"><BadgeCheck size={18} /></div>
          <div>
            <h5 className="fw-bold text-navy mb-0">{business.business_name}</h5>
            <span className="badge-verified-sm"><Check size={10} className="me-1" />Verified — Account Active</span>
          </div>
        </div>
      </ModalHeader>
      <ModalBody className="px-4 pb-2">
        <div className="detail-grid">

          {/* Business Info */}
          <div className="detail-section">
            <div className="detail-section-title">
              <Building2 size={14} /> Business Info
            </div>
            <div className="detail-rows">
              <div className="detail-row">
                <span className="detail-label">Business Name</span>
                <span className="detail-value fw-bold">{business.business_name || "—"}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">City</span>
                <span className="detail-value"><MapPin size={12} className="me-1 text-muted" />{business.city || "—"}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Verified On</span>
                <span className="detail-value">
                  <Calendar size={12} className="me-1 text-muted" />
                  {business.verified_at ? new Date(business.verified_at).toLocaleDateString("en-IN") : "—"}
                </span>
              </div>
            </div>
          </div>

          {/* Owner Info */}
          {(business.owner_name || business.email || business.mobile) && (
            <div className="detail-section">
              <div className="detail-section-title">
                <User size={14} /> Owner Details
              </div>
              <div className="detail-rows">
                {business.owner_name && (
                  <div className="detail-row">
                    <span className="detail-label">Owner Name</span>
                    <span className="detail-value fw-bold">{business.owner_name}</span>
                  </div>
                )}
                {business.email && (
                  <div className="detail-row">
                    <span className="detail-label">Email</span>
                    <span className="detail-value"><Mail size={12} className="me-1 text-muted" />{business.email}</span>
                  </div>
                )}
                {business.mobile && (
                  <div className="detail-row">
                    <span className="detail-label">Mobile</span>
                    <span className="detail-value"><Phone size={12} className="me-1 text-muted" />{business.mobile}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Documents */}
          {(business.gst_number || business.license_number) && (
            <div className="detail-section full-width">
              <div className="detail-section-title">
                <FileText size={14} /> Documents & Credentials
              </div>
              <div className="detail-rows">
                {business.gst_number && (
                  <div className="detail-row">
                    <span className="detail-label">GST Number</span>
                    <span className="detail-value mono-val"><Hash size={12} className="me-1 text-muted" />{business.gst_number}</span>
                  </div>
                )}
                {business.license_number && (
                  <div className="detail-row">
                    <span className="detail-label">License Number</span>
                    <span className="detail-value mono-val"><CreditCard size={12} className="me-1 text-muted" />{business.license_number}</span>
                  </div>
                )}
              </div>
            </div>
          )}

        </div>
      </ModalBody>
      <ModalFooter className="border-0 pt-0 px-4 pb-4">
        <Button color="light" onClick={toggle}>Close</Button>
      </ModalFooter>
    </Modal>
  );
};

/* ── Pending Tab ──────────────────────────────── */
const PendingTab = () => {
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [approving, setApproving] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [detailTarget, setDetailTarget] = useState(null);

  const limit = 10;

  const fetchPending = useCallback(async () => {
    setLoading(true);
    let url = `/verified_batch/business_list.php?page=${page}&limit=${limit}&status=PENDING`;
    if (search) url += `&q=${encodeURIComponent(search)}`;
    const res = await Handler({ method: "GET", url });
    setLoading(false);
    if (res?.success) {
      // business_list.php returns { data, total } — normalise owner_name field
      const rows = (res.data?.data || []).map((r) => ({
        ...r,
        owner_name: r.name,       // u.name → owner_name
      }));
      setList(rows);
      setTotal(res.data?.total || 0);
    }
  }, [page, search]);

  useEffect(() => { fetchPending(); }, [fetchPending]);

  const handleApprove = async (item) => {
    // Updated confirm message to reflect account activation
    if (!window.confirm(`Approve "${item.business_name}"?\n\nThis will verify their business and activate their account so they can log in.`)) return;
    setApproving(item.user_id);
    const res = await Handler({
      method: "POST",
      url: "/verified_batch/verify_respond.php",
      data: { user_id: item.user_id, action: "APPROVE" },
    });
    setApproving(null);
    if (res?.success) {
      toast.success(`✅ ${item.business_name} approved — account is now active`);
      fetchPending();
    } else {
      toast.error(res?.message || "Failed to approve");
    }
  };

  return (
    <div>
      {/* Info Banner */}
      <div className="info-banner mb-3">
        <AlertCircle size={15} className="flex-shrink-0" />
        <span>
          These accounts are <strong>inactive</strong> and cannot log in until verification is approved.
          Rejecting a request will <strong>suspend</strong> the account.
        </span>
      </div>

      <Card className="border-0 shadow-sm rounded-4 mb-4">
        <CardBody className="p-3">
          <div className="search-input-wrap">
            <Search size={18} className="search-icon" />
            <Input
              className="form-control-clean"
              placeholder="Search business, GST, license, owner..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { setPage(1); setSearch(searchInput); } }}
            />
            <Button color="navy" className="search-btn-inside" onClick={() => { setPage(1); setSearch(searchInput); }}>Search</Button>
          </div>
        </CardBody>
      </Card>

      {loading ? (
        <div className="text-center py-5"><Spinner color="primary" /></div>
      ) : list.length === 0 ? (
        <div className="text-center py-5 bg-white rounded-4 shadow-sm">
          <ShieldCheck size={48} className="text-muted opacity-25 mb-3" />
          <p className="text-muted">No pending verification requests</p>
        </div>
      ) : (
        <div className="table-responsive rounded-4 shadow-sm bg-white overflow-hidden">
          <Table hover className="align-middle mb-0 custom-table">
            <thead className="bg-light text-navy">
              <tr>
                <th className="ps-4">Business & Documents</th>
                <th>Owner Details</th>
                <th>Location</th>
                <th>Requested</th>
                <th className="text-end pe-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.map((item) => (
                <tr key={item.id}>
                  <td className="ps-4">
                    <div className="fw-bold text-navy mb-1">{item.business_name}</div>
                    {/* Account inactive indicator */}
                    <div className="account-inactive-badge mb-1">
                      <Lock size={9} /> Account Inactive
                    </div>
                    <div className="d-flex gap-2">
                      <span className="doc-badge"><Hash size={10} /> {item.gst_number || "No GST"}</span>
                      <span className="doc-badge"><FileText size={10} /> {item.license_number || "No License"}</span>
                    </div>
                  </td>
                  <td>
                    <div className="text-navy small fw-bold">{item.owner_name}</div>
                    <div className="text-muted x-small d-flex align-items-center gap-1"><Phone size={10} /> {item.mobile}</div>
                    <div className="text-muted x-small d-flex align-items-center gap-1"><Mail size={10} /> {item.email}</div>
                  </td>
                  <td>
                    <div className="text-muted small d-flex align-items-center gap-1">
                      <MapPin size={12} /> {item.city || "—"}
                    </div>
                  </td>
                  <td className="text-muted small">
                    {item.created_at ? new Date(item.created_at).toLocaleDateString("en-IN") : "—"}
                  </td>
                  <td className="text-end pe-4">
                    <div className="d-flex justify-content-end gap-2">
                      <Button
                        color="soft-info"
                        size="sm"
                        className="rounded-pill px-3"
                        onClick={() => setDetailTarget(item)}
                        title="View full details"
                      >
                        <Eye size={14} className="me-1" /> View
                      </Button>
                      <Button
                        color="soft-success"
                        size="sm"
                        className="rounded-pill px-3"
                        onClick={() => handleApprove(item)}
                        disabled={approving === item.user_id}
                        title="Approve and activate account"
                      >
                        {approving === item.user_id
                          ? <Spinner size="sm" />
                          : <><UserCheck size={13} className="me-1" />Approve</>
                        }
                      </Button>
                      <Button
                        color="soft-danger"
                        size="sm"
                        className="rounded-pill px-3"
                        onClick={() => setRejectTarget(item)}
                        disabled={approving === item.user_id}
                        title="Reject and suspend account"
                      >
                        <UserX size={13} className="me-1" />Reject
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      {/* Pending Detail Modal */}
      <PendingDetailModal
        isOpen={!!detailTarget}
        toggle={() => setDetailTarget(null)}
        business={detailTarget}
        onApprove={handleApprove}
        onReject={(item) => setRejectTarget(item)}
        approving={approving}
      />

      {rejectTarget && (
        <RejectModal
          isOpen={!!rejectTarget}
          toggle={() => setRejectTarget(null)}
          business={rejectTarget}
          onDone={() => { setRejectTarget(null); fetchPending(); }}
        />
      )}
    </div>
  );
};

/* ── Verified Tab ─────────────────────────────── */
const VerifiedTab = () => {
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [detailTarget, setDetailTarget] = useState(null);

  const limit = 10;

  const fetchVerified = useCallback(async () => {
    setLoading(true);
    let url = `/verified_batch/business_list.php?page=${page}&limit=${limit}&status=APPROVED`;
    if (search) url += `&q=${encodeURIComponent(search)}`;
    const res = await Handler({ method: "GET", url });
    setLoading(false);
    if (res?.success) {
      const rows = (res.data?.data || []).map((r) => ({
        ...r,
        owner_name: r.name,
      }));
      setList(rows);
      setTotal(res.data?.total || 0);
    }
  }, [page, search]);

  useEffect(() => { fetchVerified(); }, [fetchVerified]);

  return (
    <div>
      <Card className="border-0 shadow-sm rounded-4 mb-4">
        <CardBody className="p-3">
          <div className="search-input-wrap">
            <Search size={18} className="search-icon" />
            <Input
              className="form-control-clean"
              placeholder="Search verified business..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { setPage(1); setSearch(searchInput); } }}
            />
            <Button color="navy" className="search-btn-inside" onClick={() => { setPage(1); setSearch(searchInput); }}>Search</Button>
          </div>
        </CardBody>
      </Card>

      {loading ? (
        <div className="text-center py-5"><Spinner color="primary" /></div>
      ) : (
        <div className="table-responsive rounded-4 shadow-sm bg-white overflow-hidden">
          <Table hover className="align-middle mb-0 custom-table">
            <thead className="bg-light text-navy">
              <tr>
                <th className="ps-4">Business</th>
                <th>Location</th>
                <th>Verified On</th>
                <th className="text-end pe-4">Status</th>
              </tr>
            </thead>
            <tbody>
              {list.map((item) => (
                <tr key={item.id}>
                  <td className="ps-4">
                    <div className="fw-bold text-navy">{item.business_name}</div>
                  </td>
                  <td><MapPin size={12} className="text-muted me-1" /> {item.city || "—"}</td>
                  <td className="text-muted small">
                    <Calendar size={12} className="me-1" />
                    {item.verified_at ? new Date(item.verified_at).toLocaleDateString("en-IN") : "—"}
                  </td>
                  <td className="text-end pe-4">
                    <div className="d-flex justify-content-end align-items-center gap-3">
                      {/* Verified badge + active account pill */}
                      <div className="d-flex flex-column align-items-end gap-1">
                        <Badge color="soft-success" className="rounded-pill text-success px-3 py-2 border-0">
                          <Check size={12} className="me-1" /> Verified Business
                        </Badge>
                        <span className="account-active-badge">
                          <UserCheck size={9} /> Account Active
                        </span>
                      </div>
                      <Button
                        color="soft-info"
                        size="sm"
                        className="rounded-pill px-3"
                        onClick={() => setDetailTarget(item)}
                        title="View full details"
                      >
                        <Eye size={14} className="me-1" /> View
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      {/* Verified Detail Modal */}
      <VerifiedDetailModal
        isOpen={!!detailTarget}
        toggle={() => setDetailTarget(null)}
        business={detailTarget}
      />
    </div>
  );
};

/* ── Rejected Detail Modal ────────────────────── */
const RejectedDetailModal = ({ business, isOpen, toggle, onReApprove, reapproving }) => {
  if (!business) return null;

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="lg" className="border-0 detail-modal">
      <ModalHeader toggle={toggle} className="border-0 pb-0">
        <div className="d-flex align-items-center gap-2">
          <div className="icon-box-red"><XCircle size={18} /></div>
          <div>
            <h5 className="fw-bold text-navy mb-0">{business.business_name}</h5>
            <span className="badge-rejected-sm"><UserX size={10} className="me-1" />Rejected — Account Suspended</span>
          </div>
        </div>
      </ModalHeader>
      <ModalBody className="px-4 pb-2">

        {/* Rejection reason banner */}
        <div className="rejection-reason-banner mb-3">
          <MessageSquare size={15} className="flex-shrink-0 mt-1" />
          <div>
            <div className="fw-bold mb-1" style={{ fontSize: "0.8rem" }}>Reason for Rejection</div>
            <div style={{ fontSize: "0.85rem" }}>{business.verification_reason || "No reason provided"}</div>
          </div>
        </div>

        {/* ✅ NEW: Re-approve consequence notice */}
        <div className="approval-notice-row mb-3">
          <div className="notice-pill approve-pill">
            <UserCheck size={13} /> Re-approve → Lifts suspension &amp; re-activates account
          </div>
        </div>

        <div className="detail-grid">
          {/* Business Info */}
          <div className="detail-section">
            <div className="detail-section-title"><Building2 size={14} /> Business Info</div>
            <div className="detail-rows">
              <div className="detail-row">
                <span className="detail-label">Business Name</span>
                <span className="detail-value fw-bold">{business.business_name || "—"}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">City</span>
                <span className="detail-value"><MapPin size={12} className="me-1 text-muted" />{business.city || "—"}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Rejected On</span>
                <span className="detail-value">
                  <Calendar size={12} className="me-1 text-muted" />
                  {business.verified_at ? new Date(business.verified_at).toLocaleDateString("en-IN") : "—"}
                </span>
              </div>
            </div>
          </div>

          {/* Owner Info */}
          <div className="detail-section">
            <div className="detail-section-title"><User size={14} /> Owner Details</div>
            <div className="detail-rows">
              {business.owner_name && (
                <div className="detail-row">
                  <span className="detail-label">Owner Name</span>
                  <span className="detail-value fw-bold">{business.owner_name}</span>
                </div>
              )}
              {business.email && (
                <div className="detail-row">
                  <span className="detail-label">Email</span>
                  <span className="detail-value"><Mail size={12} className="me-1 text-muted" />{business.email}</span>
                </div>
              )}
              {business.mobile && (
                <div className="detail-row">
                  <span className="detail-label">Mobile</span>
                  <span className="detail-value"><Phone size={12} className="me-1 text-muted" />{business.mobile}</span>
                </div>
              )}
            </div>
          </div>

          {/* Documents */}
          {(business.gst_number || business.license_number) && (
            <div className="detail-section full-width">
              <div className="detail-section-title"><FileText size={14} /> Documents & Credentials</div>
              <div className="detail-rows">
                {business.gst_number && (
                  <div className="detail-row">
                    <span className="detail-label">GST Number</span>
                    <span className="detail-value mono-val"><Hash size={12} className="me-1 text-muted" />{business.gst_number}</span>
                  </div>
                )}
                {business.license_number && (
                  <div className="detail-row">
                    <span className="detail-label">License Number</span>
                    <span className="detail-value mono-val"><CreditCard size={12} className="me-1 text-muted" />{business.license_number}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </ModalBody>

      {/* ✅ UPDATED Footer with Re-approve button */}
      <ModalFooter className="border-0 pt-0 px-4 pb-4 gap-2">
        <Button color="light" onClick={toggle}>Close</Button>
        <Button
          color="soft-success"
          className="rounded-pill px-4"
          onClick={() => onReApprove(business)}
          disabled={reapproving === business.user_id}
        >
          {reapproving === business.user_id
            ? <Spinner size="sm" />
            : <><UserCheck size={14} className="me-1" /> Re-approve &amp; Activate</>
          }
        </Button>
      </ModalFooter>
    </Modal>
  );
};

/* ── Rejected Tab ─────────────────────────────── */
const RejectedTab = () => {
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [detailTarget, setDetailTarget] = useState(null);
  const [reapproving, setReapproving] = useState(null); // ✅ NEW

  const limit = 10;

  const fetchRejected = useCallback(async () => {
    setLoading(true);
    let url = `/verified_batch/business_list.php?page=${page}&limit=${limit}&status=REJECTED`;
    if (search) url += `&q=${encodeURIComponent(search)}`;
    const res = await Handler({ method: "GET", url });
    setLoading(false);
    if (res?.success) {
      const rows = (res.data?.data || []).map((r) => ({
        ...r,
        owner_name: r.name,
      }));
      setList(rows);
      setTotal(res.data?.total || 0);
    }
  }, [page, search]);

  useEffect(() => { fetchRejected(); }, [fetchRejected]);

  // ✅ NEW: Re-approve handler
  const handleReApprove = async (item) => {
    if (!window.confirm(`Re-approve "${item.business_name}"?\n\nThis will verify their business and re-activate their suspended account.`)) return;
    setReapproving(item.user_id);
    const res = await Handler({
      method: "POST",
      url: "/verified_batch/verify_respond.php",
      data: { user_id: item.user_id, action: "APPROVE" },
    });
    setReapproving(null);
    if (res?.success) {
      toast.success(`✅ ${item.business_name} re-approved — account is now active`);
      setDetailTarget(null); // close modal if open
      fetchRejected();
    } else {
      toast.error(res?.message || "Failed to re-approve");
    }
  };

  return (
    <div>
      {/* Info Banner */}
      <div className="info-banner-red mb-3">
        <Lock size={15} className="flex-shrink-0" />
        <span>
          These accounts have been <strong>rejected</strong> and their login access is <strong>suspended</strong>.
          They cannot access the platform until re-reviewed.
        </span>
      </div>

      <Card className="border-0 shadow-sm rounded-4 mb-4">
        <CardBody className="p-3">
          <div className="search-input-wrap">
            <Search size={18} className="search-icon" />
            <Input
              className="form-control-clean"
              placeholder="Search rejected business, owner..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { setPage(1); setSearch(searchInput); } }}
            />
            <Button color="navy" className="search-btn-inside" onClick={() => { setPage(1); setSearch(searchInput); }}>Search</Button>
          </div>
        </CardBody>
      </Card>

      {loading ? (
        <div className="text-center py-5"><Spinner color="primary" /></div>
      ) : list.length === 0 ? (
        <div className="text-center py-5 bg-white rounded-4 shadow-sm">
          <XCircle size={48} className="text-muted opacity-25 mb-3" />
          <p className="text-muted">No rejected verification requests</p>
        </div>
      ) : (
        <div className="table-responsive rounded-4 shadow-sm bg-white overflow-hidden">
          <Table hover className="align-middle mb-0 custom-table">
            <thead className="bg-light text-navy">
              <tr>
                <th className="ps-4">Business & Documents</th>
                <th>Owner Details</th>
                <th>Location</th>
                <th>Rejection Reason</th>
                <th>Rejected On</th>
                <th className="text-end pe-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.map((item) => (
                <tr key={item.id} className="rejected-row">
                  <td className="ps-4">
                    <div className="fw-bold text-navy mb-1">{item.business_name}</div>
                    <div className="account-suspended-badge mb-1">
                      <Lock size={9} /> Account Suspended
                    </div>
                    <div className="d-flex gap-2 mt-1">
                      <span className="doc-badge"><Hash size={10} /> {item.gst_number || "No GST"}</span>
                      <span className="doc-badge"><FileText size={10} /> {item.license_number || "No License"}</span>
                    </div>
                  </td>
                  <td>
                    <div className="text-navy small fw-bold">{item.owner_name || "—"}</div>
                    {item.mobile && <div className="text-muted x-small d-flex align-items-center gap-1"><Phone size={10} /> {item.mobile}</div>}
                    {item.email && <div className="text-muted x-small d-flex align-items-center gap-1"><Mail size={10} /> {item.email}</div>}
                  </td>
                  <td>
                    <div className="text-muted small d-flex align-items-center gap-1">
                      <MapPin size={12} /> {item.city || "—"}
                    </div>
                  </td>
                  <td style={{ maxWidth: "200px" }}>
                    <div className="rejection-reason-cell" title={item.verification_reason}>
                      {item.verification_reason || <span className="text-muted fst-italic">No reason</span>}
                    </div>
                  </td>
                  <td className="text-muted small">
                    {item.verified_at ? new Date(item.verified_at).toLocaleDateString("en-IN") : "—"}
                  </td>
                  {/* ✅ UPDATED: Actions column with Re-approve + View */}
                  <td className="text-end pe-4">
                    <div className="d-flex justify-content-end gap-2">
                      <Button
                        color="soft-info"
                        size="sm"
                        className="rounded-pill px-3"
                        onClick={() => setDetailTarget(item)}
                        title="View full details"
                      >
                        <Eye size={14} className="me-1" /> View
                      </Button>
                      <Button
                        color="soft-success"
                        size="sm"
                        className="rounded-pill px-3"
                        onClick={() => handleReApprove(item)}
                        disabled={reapproving === item.user_id}
                        title="Re-approve and re-activate account"
                      >
                        {reapproving === item.user_id
                          ? <Spinner size="sm" />
                          : <><UserCheck size={13} className="me-1" /> Re-approve</>
                        }
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      {/* ✅ UPDATED: Pass handleReApprove and reapproving into modal */}
      <RejectedDetailModal
        isOpen={!!detailTarget}
        toggle={() => setDetailTarget(null)}
        business={detailTarget}
        onReApprove={handleReApprove}   // ✅ NEW
        reapproving={reapproving}        // ✅ NEW
      />
    </div>
  );
};

/* ── Main Admin Verification Page ─────────────── */
const AdminVerification = () => {
  const [activeTab, setActiveTab] = useState("pending");

  return (
    <div className="bg-soft-blue min-vh-100 pb-5">
      <AdminHeader />
      <Container py="4" className="mt-4">

        {/* Header Section */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <div className="d-flex align-items-center mb-1">
              <div className="icon-box-navy me-2">
                <ShieldCheck size={20} />
              </div>
              <h3 className="fw-bold text-navy mb-0">Business Verification</h3>
            </div>
            <p className="text-muted small mb-0 ml-48">
              Approve to activate accounts · Reject to suspend login access
            </p>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="custom-tabs mb-4">
          <button
            className={`tab-btn ${activeTab === "pending" ? "active" : ""}`}
            onClick={() => setActiveTab("pending")}
          >
            <Clock size={16} className="me-2" /> Pending Requests
          </button>
          <button
            className={`tab-btn ${activeTab === "verified" ? "active" : ""}`}
            onClick={() => setActiveTab("verified")}
          >
            <ShieldCheck size={16} className="me-2" /> Verified Directory
          </button>
          <button
            className={`tab-btn tab-btn-danger ${activeTab === "rejected" ? "active" : ""}`}
            onClick={() => setActiveTab("rejected")}
          >
            <XCircle size={16} className="me-2" /> Rejected
          </button>
        </div>

        {/* Tab Content */}
        <TabContent activeTab={activeTab}>
          <TabPane tabId="pending">
            <PendingTab />
          </TabPane>
          <TabPane tabId="verified">
            <VerifiedTab />
          </TabPane>
          <TabPane tabId="rejected">
            <RejectedTab />
          </TabPane>
        </TabContent>

      </Container>

      <style>{`
        .bg-soft-blue { background-color: #f8fafc; }
        .text-navy { color: #1e3a8a; }
        .bg-navy, .btn-navy { background-color: #1e3a8a !important; color: white !important; }
        .icon-box-navy { 
            background: #1e3a8a; color: white; width: 38px; height: 38px; 
            display: flex; align-items: center; justify-content: center; border-radius: 10px; 
        }
        .icon-box-green {
            background: #16a34a; color: white; width: 38px; height: 38px;
            display: flex; align-items: center; justify-content: center; border-radius: 10px;
        }

        .rounded-4 { border-radius: 16px !important; }
        
        /* Tab Styling */
        .custom-tabs { display: flex; gap: 8px; border-bottom: 2px solid #e2e8f0; }
        .tab-btn { 
            padding: 12px 24px; border: none; background: none; color: #64748b; 
            font-weight: 600; border-bottom: 2px solid transparent; margin-bottom: -2px;
            transition: 0.3s;
        }
        .tab-btn.active { color: #1e3a8a; border-bottom: 2px solid #1e3a8a; }
        .tab-btn:hover { color: #1e3a8a; }

        /* Info Banner */
        .info-banner {
          display: flex;
          align-items: flex-start;
          gap: 8px;
          background: #fffbeb;
          border: 1px solid #fde68a;
          color: #92400e;
          border-radius: 10px;
          padding: 10px 14px;
          font-size: 0.82rem;
        }

        /* Consequence Warning Banner (inside Reject Modal) */
        .consequence-banner {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          background: #fef2f2;
          border: 1px solid #fecaca;
          color: #991b1b;
          border-radius: 10px;
          padding: 12px 14px;
        }

        /* Approval consequence pills (inside detail modal) */
        .approval-notice-row {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }
        .notice-pill {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          font-size: 0.75rem;
          font-weight: 600;
          padding: 4px 10px;
          border-radius: 20px;
        }
        .approve-pill { background: #dcfce7; color: #166534; }
        .reject-pill  { background: #fee2e2; color: #991b1b; }

        /* Account status micro-badges */
        .account-inactive-badge {
          display: inline-flex;
          align-items: center;
          gap: 3px;
          background: #fef3c7;
          color: #92400e;
          font-size: 0.68rem;
          font-weight: 600;
          padding: 1px 7px;
          border-radius: 20px;
        }
        .account-active-badge {
          display: inline-flex;
          align-items: center;
          gap: 3px;
          background: #dcfce7;
          color: #166534;
          font-size: 0.68rem;
          font-weight: 600;
          padding: 1px 7px;
          border-radius: 20px;
        }

        /* Search Wrap */
        .search-input-wrap { position: relative; display: flex; align-items: center; }
        .search-icon { position: absolute; left: 16px; color: #94a3b8; }
        .form-control-clean { 
            padding: 12px 100px 12px 48px !important; border-radius: 12px !important; 
            border: 1px solid #e2e8f0 !important; background: #f8fafc !important; height: 48px;
        }
        .search-btn-inside { position: absolute; right: 6px; border-radius: 8px !important; padding: 6px 16px; }

        /* Table Styling */
        .custom-table thead th { border-top: none; font-size: 0.8rem; text-transform: uppercase; letter-spacing: 0.5px; padding: 15px 10px; }
        .custom-table tbody tr { transition: 0.2s; }
        .custom-table tbody tr:hover { background-color: #f1f5f9; }
        .doc-badge { background: #f1f5f9; color: #475569; padding: 2px 8px; border-radius: 4px; font-size: 10px; font-family: monospace; display: flex; align-items: center; gap: 4px; }
        
        .x-small { font-size: 0.75rem; }
        .ml-48 { margin-left: 48px; }

        /* Soft Buttons */
        .btn-soft-success { background: #dcfce7; color: #16a34a; border: none; font-weight: 600; }
        .btn-soft-success:hover { background: #16a34a; color: white; }
        .btn-soft-danger { background: #fee2e2; color: #dc2626; border: none; font-weight: 600; }
        .btn-soft-danger:hover { background: #dc2626; color: white; }
        .btn-soft-info { background: #e0f2fe; color: #0369a1; border: none; font-weight: 600; }
        .btn-soft-info:hover { background: #0369a1; color: white; }
        
        .badge-soft-success { background: #dcfce7; color: #16a34a; }

        /* Detail Modal Styles */
        .detail-modal .modal-content { border-radius: 16px !important; border: none; box-shadow: 0 25px 50px -12px rgba(0,0,0,0.2); }
        .detail-modal .modal-header { padding: 20px 24px 12px; }

        .detail-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
          margin-top: 8px;
        }
        .detail-section {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 16px;
        }
        .detail-section.full-width { grid-column: 1 / -1; }
        .detail-section-title {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 0.75rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.6px;
          color: #64748b;
          margin-bottom: 12px;
          padding-bottom: 8px;
          border-bottom: 1px solid #e2e8f0;
        }
        .detail-rows { display: flex; flex-direction: column; gap: 10px; }
        .detail-row { display: flex; justify-content: space-between; align-items: center; gap: 12px; }
        .detail-label { font-size: 0.8rem; color: #94a3b8; white-space: nowrap; flex-shrink: 0; }
        .detail-value { font-size: 0.85rem; color: #1e293b; text-align: right; display: flex; align-items: center; }
        .mono-val { font-family: monospace; font-size: 0.82rem; }

        /* Status badges inside modal header */
        .badge-pending-sm {
          display: inline-block;
          background: #fef3c7;
          color: #b45309;
          font-size: 0.7rem;
          font-weight: 600;
          padding: 2px 8px;
          border-radius: 20px;
          margin-top: 2px;
        }
        .badge-verified-sm {
          display: inline-flex;
          align-items: center;
          background: #dcfce7;
          color: #16a34a;
          font-size: 0.7rem;
          font-weight: 600;
          padding: 2px 8px;
          border-radius: 20px;
          margin-top: 2px;
        }

        /* Rejected tab icon box */
        .icon-box-red {
          background: #dc2626; color: white; width: 38px; height: 38px;
          display: flex; align-items: center; justify-content: center; border-radius: 10px;
        }

        /* Rejected badge in modal header */
        .badge-rejected-sm {
          display: inline-flex;
          align-items: center;
          background: #fee2e2;
          color: #991b1b;
          font-size: 0.7rem;
          font-weight: 600;
          padding: 2px 8px;
          border-radius: 20px;
          margin-top: 2px;
        }

        /* Rejection reason banner inside modal */
        .rejection-reason-banner {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          background: #fff7ed;
          border: 1px solid #fed7aa;
          color: #9a3412;
          border-radius: 10px;
          padding: 12px 14px;
        }

        /* Red info banner for rejected tab */
        .info-banner-red {
          display: flex;
          align-items: flex-start;
          gap: 8px;
          background: #fef2f2;
          border: 1px solid #fecaca;
          color: #991b1b;
          border-radius: 10px;
          padding: 10px 14px;
          font-size: 0.82rem;
        }

        /* Account suspended micro-badge */
        .account-suspended-badge {
          display: inline-flex;
          align-items: center;
          gap: 3px;
          background: #fee2e2;
          color: #991b1b;
          font-size: 0.68rem;
          font-weight: 600;
          padding: 1px 7px;
          border-radius: 20px;
        }

        /* Rejection reason truncated cell */
        .rejection-reason-cell {
          font-size: 0.8rem;
          color: #64748b;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 180px;
        }

        /* Rejected row subtle tint */
        .rejected-row { background-color: #fffafa; }
        .rejected-row:hover { background-color: #fef2f2 !important; }

        /* Rejected tab button red active state */
        .tab-btn-danger.active { color: #dc2626 !important; border-bottom-color: #dc2626 !important; }
        .tab-btn-danger:hover { color: #dc2626 !important; }
      `}</style>
    </div>
  );
};

export default AdminVerification;